import React, { useCallback, useEffect, useState, useMemo } from "react";

import { CircularProgress, Grid, IconButton, Tooltip } from "@mui/material";
import { format } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";

import FieldView from "../../../../../../../../components/Form/FieldView";
import BadgeJobStatus from "../../../../../../../../components/Generic/BadgeJobStatus";
import GeneralCommentsComponent from "../../../../../../../../components/Generic/GeneralComments";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Task } from "../../../../../../../../models/task";
import { convertMetricVolume } from "../../../../../../../../util/converts";
import { TaskStatus } from "../../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";
import { isAllied } from "../../../../../../../../util/isAllied";
import {
  getBreakTime,
  getTransitTime,
  getWorkTime,
  getTotalTime,
} from "../../../../../../../../util/timeControl";
import { TaskStartDatePeriod } from "Models/task";
import TaskDateUtils from "Util/dateUtils";

import useStyles, {
  TaskContent,
  TimeControlTitle,
  TimeControlWrapper,
  GeneralWrapper,
  MultiDayTaskBadge,
} from "./styles";

const CORNOR_UP_RIGHT_ICON = require("../../../../../../../../assets/img/corner-up-right.svg");

interface TaskViewProps {
  task?: Task;
  expand?: boolean;
  isLoading?: boolean;
  taskTypeSequence?: number;
  sendSistrsEmail: (id: number) => void;
}

export default function TaskViewMode(props: TaskViewProps) {
  const { task, expand, isLoading = false, taskTypeSequence, sendSistrsEmail } = props;
  const { t: translate } = useTranslation();
  const { currentUser, userTimezone, defaultTimezone } = useAppState();

  const [openInfo, setOpenInfo] = useState<boolean>(false);

  const classes = useStyles((expand || openInfo) ?? false)();

  const hasTaskMultiDayPeriod = useMemo(() => {
    if (!task?.period) return false;
    return !![
      TaskStartDatePeriod.FIXED_TIME,
      TaskStartDatePeriod.MULTI_DAY,
      TaskStartDatePeriod.VARIABLE_TIME,
    ].includes(task?.period);
  }, [task]);

  const dateToDisplay = useCallback(
    (date?: string) => {
      if (!date || !TaskDateUtils.isValidDate(date)) return "";

      return TaskDateUtils.formatForDisplay(date, undefined, {
        timezone: userTimezone?.name || defaultTimezone,
      });
    },
    [task]
  );

  const rangeTimeToDisplay = useMemo(() => {
    if (!task?.startDate || !TaskDateUtils.isValidDate(task.startDate)) return "";

    const startLabel = TaskDateUtils.formatForDisplay(task.startDate, "hh:mm a", {
      timezone: userTimezone?.name || defaultTimezone,
    });
    const endLabel =
      task.endDate && TaskDateUtils.isValidDate(task.endDate)
        ? TaskDateUtils.formatForDisplay(task.endDate, "hh:mm a", {
            timezone: userTimezone?.name || defaultTimezone,
          })
        : "";

    return `${startLabel}${endLabel ? ` ~ ${endLabel}` : ""}`;
  }, [task]);

  const renderSendToSISTRSbutton = () => {
    if (
      task?.id &&
      task?.status === TaskStatus.FINISHED &&
      process.env.NODE_DATABASE === "production" &&
      isAllied(currentUser?.company)
    ) {
      return (
        <Tooltip title={translate("sendTaskToSistrs")} placement="top">
          <IconButton
            className={classes.sendToSISTRS}
            onClick={() => sendSistrsEmail(Number(task?.id))}
          >
            {isLoading ? (
              <CircularProgress className={classes.loadingSISTRS} size={22} />
            ) : (
              <img src={CORNOR_UP_RIGHT_ICON} width="22" height="22" alt="cornor up right icon" />
            )}
          </IconButton>
        </Tooltip>
      );
    }
  };

  const returnLeaderName = (): string => {
    if (task?.leader) {
      const { name, lastName } = task.leader;

      return `${name ?? ""} ${lastName ?? ""}`;
    }

    return "";
  };

  const returnStaffOrFleetArray = (type: string) => {
    let newArray: string[] = [];

    if (type === "staff") {
      if (task?.staffs && task?.staffs.length) {
        newArray = task.staffs?.map((staff) => `${staff.firstName} ${staff.lastName ?? ""}`);
      }
    } else if (type === "fleet") {
      if (task?.fleets && task?.fleets.length) {
        newArray = task?.fleets?.map(
          (fleet) =>
            `${fleet.fleetNumber ?? ""} ${fleet.model ?? ""} - ${
              convertMetricVolume(Number(fleet.capacityM3), getMeasurement()?.code, 1) ?? ""
            }${getMeasurement()?.code}`
        );
      }
    } else if (type === "extraStaff") {
      if (task?.extraStaffs && task?.extraStaffs.length) {
        newArray = task.extraStaffs?.map((staff) => `${staff.firstName} ${staff.lastName ?? ""}`);
      }
    }

    if (newArray.length) {
      if (newArray.length > 2) {
        const firstTwoItems = newArray.slice(0, 2);
        const lastItems = newArray.slice(2, newArray.length);

        return { value: firstTwoItems, tooltip: lastItems?.join(", ") };
      }

      return { value: newArray };
    }

    return { value: undefined };
  };

  const renderFormatTime = (date: string, textFormat: string) => {
    if (!date || !TaskDateUtils.isValidDate(date)) return "";

    return format(
      utcToZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone),
      textFormat
    );
  };

  const handleExpandTask = useCallback(
    (expand: boolean) => {
      setOpenInfo(expand);
    },
    [expand]
  );

  const returnTimeSection = useCallback(() => {
    if (hasTaskMultiDayPeriod) {
      return (
        <React.Fragment>
          <Grid item sm={12} md={6} lg={4}>
            <FieldView label={translate("startDate")} value={dateToDisplay(task?.startDate)} />
          </Grid>
          <Grid item sm={12} md={6} lg={4}>
            <FieldView label={translate("endDate")} value={dateToDisplay(task?.endDate)} />
          </Grid>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Grid item sm={12} md={6} lg={4}>
          <FieldView label="Date" value={dateToDisplay(task?.startDate)} />
        </Grid>
        <Grid item sm={12} md={6} lg={4}>
          <FieldView label="Time" value={rangeTimeToDisplay} />
        </Grid>
      </React.Fragment>
    );
  }, [task, hasTaskMultiDayPeriod]);

  useEffect(() => {
    handleExpandTask(expand ?? false);
  }, [expand]);

  return (
    <div className={classes.wrapper}>
      {/* Header */}
      <Grid container direction="row" alignItems="center" justifyContent="space-between">
        <Grid item xs={6}>
          <section className={classes.headerWrapper}>
            <span className={classes.taskTitle}>
              {task?.type} {taskTypeSequence && `(${taskTypeSequence})`}
            </span>
            <BadgeJobStatus value={task?.status} />
            {hasTaskMultiDayPeriod && (
              <MultiDayTaskBadge>{translate("multi_day")}</MultiDayTaskBadge>
            )}
          </section>
        </Grid>
        <Grid item container xs={6} direction="row" alignItems="flex-end" justifyContent="flex-end">
          <Grid item>{renderSendToSISTRSbutton()}</Grid>

          <Grid item>
            <IconButton
              onClick={() => setOpenInfo((state) => !state)}
              className={classes.expandIcon}
            >
              {openInfo ? <HiChevronUp /> : <HiChevronDown />}
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      {/* Body */}
      <section className={classes.wrapperSection}>
        {openInfo && (
          <TaskContent>
            <Grid container direction="row" spacing={2}>
              <Grid item container sm={12} md={12} lg={12} spacing={2}>
                {/* Time section */}
                {returnTimeSection()}
                <Grid item sm={12} md={6} lg={4}>
                  <FieldView label="Leader" value={returnLeaderName()} />
                </Grid>
              </Grid>
              <Grid item container sm={12} md={12} lg={12} spacing={2}>
                <Grid item sm={12} md={6} lg={4}>
                  <FieldView
                    label={translate("staff")}
                    valueClass={classes.tag}
                    value={returnStaffOrFleetArray("staff")?.value}
                    tooltip={returnStaffOrFleetArray("staff")?.tooltip}
                  />
                </Grid>
                <Grid item sm={12} md={6} lg={8}>
                  <FieldView
                    label={translate("fleet")}
                    valueClass={classes.tag}
                    value={returnStaffOrFleetArray("fleet")?.value}
                    tooltip={returnStaffOrFleetArray("fleet")?.tooltip}
                  />
                </Grid>
              </Grid>
            </Grid>
            <TimeControlWrapper>
              <TimeControlTitle>{translate("timeControl")}</TimeControlTitle>
              <Grid container direction="row" spacing={2} className={classes.m16}>
                <Grid item xs={12}>
                  <FieldView
                    label={translate("arrivedAtTheResidence")}
                    value={
                      task?.arriveResidence && renderFormatTime(task?.arriveResidence, "HH:mm aa")
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <FieldView
                    label={translate("breakFrom")}
                    value={task?.breakTake && renderFormatTime(task?.breakTake, "HH:mm aa")}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <FieldView
                    label={translate("to")}
                    value={task?.breakBack && renderFormatTime(task?.breakBack, "HH:mm aa")}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <FieldView
                    label={translate("leavingTheResidence")}
                    value={
                      task?.leavingResidence && renderFormatTime(task?.leavingResidence, "HH:mm aa")
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <FieldView
                    label={translate("arrivedBackToTheWarehouse")}
                    value={
                      task?.arriveBackWarehouse &&
                      renderFormatTime(task?.arriveBackWarehouse, "HH:mm aa")
                    }
                  />
                </Grid>
              </Grid>

              <TimeControlTitle>{translate("timeSummary")}</TimeControlTitle>
              <Grid container direction="row" spacing={2} className={classes.m16}>
                <Grid item xs={12} md={3} lg={3}>
                  <FieldView
                    label={translate("transitTime")}
                    value={getTransitTime(task) as string}
                  />
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                  <FieldView label={translate("breakTime")} value={getBreakTime(task) as string} />
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                  <FieldView label={translate("workTime")} value={getWorkTime(task) as string} />
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                  <FieldView label={translate("totalTime")} value={getTotalTime(task) as string} />
                </Grid>
              </Grid>

              <Grid container direction="row" spacing={2} className={classes.m16}>
                <Grid item xs={12} md={6} lg={6}>
                  <TimeControlTitle>{translate("staff")}</TimeControlTitle>
                  <FieldView
                    valueClass={classes.tag}
                    value={returnStaffOrFleetArray("staff")?.value}
                    tooltip={returnStaffOrFleetArray("staff")?.tooltip}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <TimeControlTitle>{translate("extraStaff")}</TimeControlTitle>
                  <FieldView
                    valueClass={classes.tag}
                    value={returnStaffOrFleetArray("extraStaff")?.value}
                    tooltip={returnStaffOrFleetArray("extraStaff")?.tooltip}
                  />
                </Grid>
              </Grid>
            </TimeControlWrapper>
            <GeneralWrapper>
              <GeneralCommentsComponent
                enableEmptyState={false}
                generalCommentsTitle={translate("comments")}
                comments={task?.comments}
                isViewMode
              />
            </GeneralWrapper>
          </TaskContent>
        )}
      </section>
    </div>
  );
}
